import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import LoadingSpinnerLight from 'src/common/components/loading-spinner/light';
import { apiGetGongObjects } from 'src/utils/journeyApi';
import { Provider, Token, useApiIntegrations } from 'src/common/api/use-integrations.hook';
import { IntegrationConnectButton } from './connect-button';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { GongIntegrationIcon } from 'src/monet/icons';
import { MutualActionPlanDatePicker } from '../mutual-action-plans/components/date-picker';
import { Nullable } from 'src/types/nullable.type';
import { functionNoop } from 'src/utils/function/noop';
import { useCurrentSubscription } from 'src/store/subscription';
import OrganizationLink from 'src/common/OrganizationLink';


type GongCallExtensiveResponse = {
  media: {
    audioUrl: string;
    videoUrl: string;
  },
  metaData: {
    clientUniqueId: string;
    customData: any;
    direction: string;
    id: string;
    isPrivate: boolean;
    language: string;
    media: string;
    meetingUrl: string;
    primaryUserId: string;
    purpose: string;
    scheduled: string;
    scope: string;
    sdrDisposition: string;
    started: string;
    system: string;
    title: string;
    url: string;
    workspaceId: string;
  }
}


type Props = {
  onNewVideo: (url: string, metaData: any, importParams?: any) => void;
};

export const IntegrationGongPanel = ({
  onNewVideo
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [callsLoaded, setCallsLoaded] = useState(false);
  const [calls, setCalls] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [fromDateTime, setFromDateTime] = useState<Nullable<string>>(null);
  const [toDateTime, setToDateTime] = useState<Nullable<string>>(null);

  const { fetchAccessTokens } = useApiIntegrations();
  const [tokens, setTokens] = useState<Token[]>([]);  

  const {isEnterprise, isOnTrial} = useCurrentSubscription(state=>state);

  const loadTokens = () => {
    fetchAccessTokens('gong').then((tokens: Token[]) => {
      setTokens(tokens);
      setLoaded(true);
    }).catch((err) => {
      setLoaded(true);
    });
  }

  useEffect(() => {
    loadTokens();
  }, []);  

  // check if scroll reached the bottom with 40px offset
  const isScrollAtBottom = scrollRef.current
    ? scrollRef.current?.scrollHeight - scrollRef.current?.scrollTop - scrollRef.current?.clientHeight < 40
    : false;  

  const apiGetGongCalls = () => {
    return apiGetGongObjects('calls/extensive');
  }

  const onSelect = (call: any) => {
    const { media, parties, metaData } = call;

    const meta = {
      duration: metaData.duration,
      name: metaData.title,
    };

    const importParams = {
      source: 'gong',
      url: metaData.url,
      data: {
        callId: metaData.id,
        ...(parties && { parties }),
      }
    };

    onNewVideo(media.videoUrl, meta, importParams);
  }

  const filterOutPrivateCalls = (calls: GongCallExtensiveResponse[]) => {
    return calls.filter((call) => {
      const { metaData } = call;
      return !metaData.isPrivate;
    });
  }

  useEffect(() => {
    apiGetGongCalls().then((response) => {
      console.log("response", response);
      const { calls } = response;
      const publicCalls = filterOutPrivateCalls(calls);
      if (publicCalls) {
        setCalls(publicCalls);
      } else {
        setCalls([]);
      }
      setCallsLoaded(true);
    }).catch(()=> {
      setCallsLoaded(true);
    });
  }, [tokens]);

  if(!isEnterprise && !isOnTrial){
    return(
      <div className="flex flex-col space-y-4 text-neue-journey-fg">
        <div className="text-neue-journey-small">
          You can connect with Gong to quickly import content into Journey. Upgrade to Enterprise plan to use Gong Integration
        </div>
        <OrganizationLink to='/billing'>
              <button className='btn-bedrock-secondary self-start flex space-x-1'>
                <span>Upgrade to Enterprise</span>
              </button>
          </OrganizationLink>      
          </div>
    )
  }

  if (loaded && tokens.length === 0) {
    return (
      <div className="flex flex-col space-y-4 text-neue-journey-fg">
        <div className="text-neue-journey-small">
          Connect Gong account to quickly import content into Journey
        </div>
        <IntegrationConnectButton onIntegrationConnected={loadTokens} provider={'gong'} />
      </div>
    )
  }

  return (
    <div className='flex flex-col space-y-4 h-full w-full overflow-y-auto max-h-[25vh]' ref={scrollRef}>
      {callsLoaded && tokens.length > 0 && (
        <>
          <div className="text-neue-journey-small">Connected to: {tokens[0].email}</div>
          {/* <div className='flex items-center space-x-2'>
            <MutualActionPlanDatePicker
              onClick={functionNoop}
              dueDate={fromDateTime}
              enableRemovingDate={true}
              className={classNames(
                'rounded-md transition-colors bg-neue-canvas-fg-10 px-2 py-1 text-neue-journey-medium',
              )}
              onRemoveDueDate={() => {
                setFromDateTime(null);
              }}
              onChange={(date) => {
                setFromDateTime(date);
              }}
              label='Start date'
              activeTextClasses='text-neue-journey-fg'
              inactiveTextClasses='text-neue-journey-fg-50'
            />
            <MutualActionPlanDatePicker
              onClick={functionNoop}
              dueDate={toDateTime}
              enableRemovingDate={true}
              className={classNames(
                'rounded-md transition-colors bg-neue-canvas-fg-10 px-2 py-1 text-neue-journey-medium',
              )}
              onRemoveDueDate={() => {
                setToDateTime(null);
              }}
              onChange={(date) => {
                setToDateTime(date);
              }}
              label='End date'
              activeTextClasses='text-neue-journey-fg'
              inactiveTextClasses='text-neue-journey-fg-50'
            />
          </div> */}
        </>
      )}
      {calls.length === 0 && callsLoaded ? (
        <span className='text-neue-journey-small'>
          Gong calls in your account will appear here
        </span>
      ) : (
        <div className='flex flex-col w-full'>          
          {calls.map((call: any) => (
            <div
              className='relative px-2 py-[9px] hover:bg-neue-journey-fg-10 rounded transition-colors cursor-pointer truncate'
              onClick={() => onSelect(call)}
            >
              <div className='flex items-center space-x-2'>
                <GongIntegrationIcon className="w-5 h-5 flex-shrink-0" />
                <div className='text-neue-journey-medium truncate'>{call?.metaData?.title}</div>
              </div>
            </div>    
          ))}
        </div>
      )}
      {(!loaded || !callsLoaded) && <LoadingSpinner containerClassName={classNames('h-full')} />}
    </div>
  )
}